import React, { useMemo } from 'react';

import {
  Avatar, Button, Stack, Typography,
  useColorScheme,
} from '@mui/joy';
import { RiArrowRightSLine } from 'react-icons/ri';
import { IoReaderOutline } from 'react-icons/io5';
import { Link } from 'react-router-dom';
import greetingFactory from 'utils/Greeting';
import { ATypography } from 'Landing';
import avatar from 'assets/avatar.webp';
// import avatarMin from 'assets/avatar.min.webp';
import details from 'assets/Details';
import { useMobileMode } from '../Responsive';

export const visitors = [
  'stranger',
  'collaborator',
  'developer',
  'human',
  'visitor',
  'friend',
];

export default function Header() {
  const mobile = useMobileMode();

  const visitor = useMemo(() => visitors[Math.floor(Math.random() * visitors.length)], []);
  const greeting = useMemo(greetingFactory, []);

  const { colorScheme } = useColorScheme();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dark = useMemo(() => colorScheme === 'dark', [colorScheme]);

  // const currentCompany = useMemo(
  //  () => details.experience.find((experience) => experience.end === 'Present') || undefined, []
  // );

  return (
    <Stack
      gap="20px"
      alignItems={mobile ? 'center' : 'flex-start'}
      sx={(theme) => ({
        position: 'relative',
        '&::before': mobile ? {} : {
          content: '""',
          position: 'absolute',
          top: 'calc(70px + 33px)',
          left: '-50px',
          height: 'calc(100% + 80px - 70px - 33px)',
          width: '2px',
          background: `linear-gradient(to bottom, color-mix(in srgb, ${theme.palette.primary[500]}, ${theme.palette.background.body} 40%), ${theme.palette.primary[500]} 30%)`,
        },
      })}
    >
      <Button
        size="lg"
        component={Link}
        variant="outlined"
        to="/resume"
        startDecorator={(
          <Avatar size="lg" color="primary" sx={(theme) => ({ border: `1px solid rgba(${theme.palette.primary.mainChannel} / 0.4)` })}>
            <IoReaderOutline />
          </Avatar>
          )}
        endDecorator={(
          <RiArrowRightSLine size="1.3em" />
          )}
        sx={(theme) => ({
          '&:not(:hover)': {
            backgroundColor: `color-mix(in srgb, ${theme.palette.primary.softBg}, transparent 50%)`,
          },
          '&:hover, &:active': {
            boxShadow: `0 0 40px 5px rgba(${theme.vars.palette.primary.mainChannel} / 0.2)`,
            '& > span > svg': {
              transform: 'translateX(.6rem) scale(1.2)',
              filter: `drop-shadow(-.3rem 0 0 rgba(${theme.vars.palette.primary.mainChannel} / 0.7)) drop-shadow(-.3rem 0 0 rgba(${theme.vars.palette.primary.mainChannel} / 0.7))`,
            },
          },
          '&:active > span > svg': {
            transform: 'translateX(.6rem) scale(1.1)',
          },
          '& > span > svg': {
            transition: 'all ease .2s',
          },
          position: 'relative',
          borderColor: `color-mix(in srgb, ${theme.palette.primary[500]}, ${theme.palette.background.body} 40%)`,
          borderWidth: '2px',
          borderRadius: '100px',
          width: 'fit-content',
          maxWidth: '100%',
          backdropFilter: 'blur(5px)',
          transition: 'all ease .2s',
          flexShrink: 0,
          padding: 1,
          paddingRight: 2,
          '&::before': mobile ? {} : {
            position: 'absolute',
            content: '""',
            top: '50%',
            left: '-50px',
            width: '50px',
            height: '70px',
            marginLeft: '-2px',
            border: `2px solid color-mix(in srgb, ${theme.palette.primary[500]}, ${theme.palette.background.body} 40%)`,
            borderBottom: 'none',
            borderRight: 'none',
            borderTopLeftRadius: '50px',
          },
        })}
      >
        <Stack alignItems="flex-start" sx={{ textAlign: 'left', minWidth: 0, flex: '1 1 100%' }}>
          <Typography level="body1" textColor="inherit" fontWeight="lg" lineHeight="1.2em">
            Access online resume
          </Typography>
          <Typography
            level="body2"
            textColor="inherit"
            fontWeight="sm"
            sx={{
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              maxWidth: '100%',
              opacity: 0.8,
            }}
          >
            Dynamic, interactive, and up-to-date
          </Typography>
        </Stack>
      </Button>
      <Typography
        level={mobile ? 'h2' : 'display2'}
        fontWeight="md"
        display="flex"
        justifyContent={mobile ? 'center' : 'initial'}
        textColor={dark ? 'text.primary' : '#051825'}
        textAlign={mobile ? 'center' : 'initial'}
        fontFamily="'Fira Code', monospace"
        flexWrap="wrap"
      >
        {`${greeting}, ${visitor}`}
      </Typography>
      <Typography
        level={mobile ? 'h5' : 'h4'}
        textColor={dark ? 'text.tertiary' : '#3c617b'}
        sx={{
          position: 'relative',
          textAlign: mobile ? 'center' : 'left',
        }}
      >
        The name&apos;s
        {' '}
        <ATypography textColor="primary.plainColor" href={details.contact.linkedin}>
          {`${details.name.first} ${details.name.last}`}
        </ATypography>
        {' '}
        , but you can call me
        {' '}
        <ATypography href={details.contact.github}>
          {details.name.nickname}
        </ATypography>
      </Typography>
      <Stack
        direction={mobile ? 'column' : 'row'}
        gap={mobile ? '24px' : '60px'}
        alignItems={mobile ? 'center' : 'flex-start'}
        sx={{ marginTop: '16px' }}
      >
        <Typography
          level={mobile ? 'body1' : 'h5'}
          textColor={dark ? 'text.secondary' : '#4a6c7a'}
          sx={{
            textAlign: mobile ? 'center' : 'left',
            lineHeight: mobile ? '1.6em' : '1.7em',
            maxWidth: mobile ? '100%' : '600px',
            flex: 1,
          }}
        >
          I am a passionate developer with over 8 years of experience in building scalable,
          user-focused solutions. I specialize in
          {' '}
          <Typography component="span" textColor="primary.plainColor" fontWeight="md">
            Full Stack Development
          </Typography>
          {' '}
          and
          {' '}
          <Typography component="span" textColor="primary.plainColor" fontWeight="md">
            AI-driven technologies
          </Typography>
          , turning complex problems into elegant, impactful applications.
          <br />
          <br />
          My core expertise are focused on
          {' '}
          <Typography component="span" textColor="primary.plainColor" fontWeight="md">
            AI-driven Applications
          </Typography>
          ,
          {' '}
          <Typography component="span" textColor="primary.plainColor" fontWeight="md">
            AI Agent Systems
          </Typography>
          {' '}
          combined with RAG and MCP,
          {' '}
          <Typography component="span" textColor="primary.plainColor" fontWeight="md">
            Automated Workflows
          </Typography>
          {' '}
          and
          {' '}
          <Typography component="span" textColor="primary.plainColor" fontWeight="md">
            Voice Agent Systems
          </Typography>
          {' '}
          which help drive costs down and obtain ROI.
          <br />
          <br />
          I believe great tech starts with understanding the why behind a project.
          I blend technical expertise with a focus on business outcomes, delivering
          solutions that don&apos;t just work - they win.
        </Typography>
        <Avatar
          src={avatar}
          alt={`${details.name.first} ${details.name.last}`}
          sx={{
            width: mobile ? '200px' : '300px',
            height: mobile ? '200px' : '300px',
            flexShrink: 0,
            border: (theme) => `3px solid rgba(${theme.vars.palette.primary.mainChannel} / 0.3)`,
            borderRadius: '50%',
            boxShadow: (theme) => `0 10px 40px rgba(${theme.vars.palette.primary.mainChannel} / 0.15)`,
            transition: 'all ease 0.3s',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: (theme) => `0 15px 50px rgba(${theme.vars.palette.primary.mainChannel} / 0.25)`,
            },
          }}
        />
      </Stack>
    </Stack>
  );
}
